import { debounce } from 'lodash';
import { acceptObjectiveStateChangeMutation } from '../resources/ObjectiveState/mutations';
import { acceptScoreChangesMutation } from '../resources/Ballot/mutations';
import { ObjectiveStateResourceType } from '../resources/ObjectiveState';

type QueuedUpdate =
  | { type: 'objectiveState'; data: Partial<ObjectiveStateResourceType>; receivedAt: number }
  | { type: 'scores'; data: Record<string, { score: number; strictScore: number }>; receivedAt: number };

let delayMs = 0;
let queue: QueuedUpdate[] = [];
let intervalId: ReturnType<typeof setInterval> | null = null;
let isInitialized = false;

const SCORES_DEBOUNCE_MS = 800;

let debouncedApplyScores = debounce(
  (scores: Record<string, { score: number; strictScore: number }>) => {
    acceptScoreChangesMutation(scores);
  },
  SCORES_DEBOUNCE_MS,
);

function applyUpdate(update: QueuedUpdate) {
  if (update.type === 'objectiveState') {
    acceptObjectiveStateChangeMutation(update.data);
  } else if (update.type === 'scores') {
    debouncedApplyScores(update.data);
  }
}

function processQueue() {
  const now = Date.now();
  const ready: QueuedUpdate[] = [];
  const pending: QueuedUpdate[] = [];

  for (const update of queue) {
    if (now - update.receivedAt >= delayMs) {
      ready.push(update);
    } else {
      pending.push(update);
    }
  }

  queue = pending;

  for (const update of ready) {
    applyUpdate(update);
  }

  if (queue.length === 0 && intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

function startInterval() {
  if (intervalId === null && queue.length > 0) {
    intervalId = setInterval(processQueue, 1000);
  }
}

export function queueUpdate(
  type: 'objectiveState',
  data: Partial<ObjectiveStateResourceType>,
): void;
export function queueUpdate(
  type: 'scores',
  data: Record<string, { score: number; strictScore: number }>,
): void;
export function queueUpdate(
  type: 'objectiveState' | 'scores',
  data: Partial<ObjectiveStateResourceType> | Record<string, { score: number; strictScore: number }>,
): void {
  if (!isInitialized) {
    applyUpdate({ type, data, receivedAt: Date.now() } as QueuedUpdate);
    isInitialized = true;
    return;
  }

  if (delayMs === 0) {
    applyUpdate({ type, data, receivedAt: Date.now() } as QueuedUpdate);
    return;
  }

  queue.push({ type, data, receivedAt: Date.now() } as QueuedUpdate);
  startInterval();
}

export function setDelayMs(ms: number) {
  if (ms !== delayMs) {
    for (const update of queue) {
      applyUpdate(update);
    }
    queue = [];

    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }

  delayMs = ms;
  isInitialized = true;
}

export function getDelayMs() {
  return delayMs;
}
